/**
 * Parser OFX no cliente (SGML/XML).
 * Extrai lançamentos de BANKTRANLIST / CREDITCARDMSGSRSV1 sem enviar o arquivo ao servidor.
 */

export interface OfxTransaction {
  /** ID estável do lançamento no extrato (FITID ou hash local) */
  id: string;
  fitId?: string;
  date: string; // YYYY-MM-DD
  amount: number;
  type: 'credit' | 'debit';
  trnType?: string;
  description: string;
  memo?: string;
  name?: string;
  checkNum?: string;
  raw?: string;
}

export interface OfxStatement {
  bankId?: string;
  accountId?: string;
  accountType?: string;
  currency?: string;
  startDate?: string;
  endDate?: string;
  balance?: number;
  balanceDate?: string;
  transactions: OfxTransaction[];
}

const tagValue = (block: string, tag: string): string | undefined => {
  const re = new RegExp(`<${tag}>([^<\\r\\n]*)`, 'i');
  const m = block.match(re);
  if (!m) return undefined;
  return m[1].trim().replace(/&amp;/g, '&').replace(/&lt;/g, '<').replace(/&gt;/g, '>').replace(/&quot;/g, '"');
};

/** OFX: YYYYMMDD[HHMMSS[.XXX]][tz] → YYYY-MM-DD */
export const parseOfxDate = (value?: string): string | undefined => {
  if (!value) return undefined;
  const digits = value.replace(/[^0-9]/g, '');
  if (digits.length < 8) return undefined;
  const y = digits.slice(0, 4);
  const mo = digits.slice(4, 6);
  const d = digits.slice(6, 8);
  return `${y}-${mo}-${d}`;
};

const parseAmount = (value?: string): number => {
  if (!value) return 0;
  const normalized = value.trim().replace(/\s/g, '').replace(',', '.');
  const n = Number(normalized);
  return Number.isFinite(n) ? n : 0;
};

const hashId = (parts: string[]): string => {
  const s = parts.join('|');
  let h = 0;
  for (let i = 0; i < s.length; i++) {
    h = (Math.imul(31, h) + s.charCodeAt(i)) | 0;
  }
  return `local-${Math.abs(h).toString(36)}`;
};

const extractBlocks = (content: string, tag: string): string[] => {
  const upper = content; // already uppercased tags ideally
  const open = `<${tag}>`;
  const close = `</${tag}>`;
  const blocks: string[] = [];
  let searchFrom = 0;

  while (true) {
    const start = upper.indexOf(open, searchFrom);
    if (start === -1) break;
    const afterOpen = start + open.length;
    const end = upper.indexOf(close, afterOpen);
    if (end !== -1) {
      blocks.push(upper.slice(afterOpen, end));
      searchFrom = end + close.length;
      continue;
    }
    // SGML sem fechamento: até o próximo <STMTTRN> ou fim da lista
    const next = upper.indexOf(open, afterOpen);
    const listEnd = upper.search(/<\/BANKTRANLIST>|<\/CCSTMTRS>|<\/STMTRS>/i);
    let cut = upper.length;
    if (next !== -1) cut = Math.min(cut, next);
    if (listEnd !== -1) cut = Math.min(cut, listEnd);
    blocks.push(upper.slice(afterOpen, cut));
    searchFrom = cut;
  }

  return blocks;
};

const normalizeOfxBody = (text: string): string => {
  const ofxIdx = text.search(/<OFX>/i);
  let body = ofxIdx >= 0 ? text.slice(ofxIdx) : text;
  // Remove tags de fechamento soltas desnecessárias já tratadas; unifica espaços de quebra
  return body.replace(/\r\n/g, '\n').replace(/\r/g, '\n');
};

export const parseOfx = (fileContent: string): OfxStatement => {
  const body = normalizeOfxBody(fileContent);

  const bankId = tagValue(body, 'BANKID');
  const accountId = tagValue(body, 'ACCTID');
  const accountType = tagValue(body, 'ACCTTYPE');
  const currency = tagValue(body, 'CURDEF') || tagValue(body, 'CURRENCY');
  const startDate = parseOfxDate(tagValue(body, 'DTSTART'));
  const endDate = parseOfxDate(tagValue(body, 'DTEND'));
  const balance = (() => {
    const bal = tagValue(body, 'BALAMT');
    return bal !== undefined ? parseAmount(bal) : undefined;
  })();
  const balanceDate = parseOfxDate(tagValue(body, 'DTASOF'));

  const stmtBlocks = extractBlocks(body, 'STMTTRN');
  const seen = new Set<string>();

  const transactions: OfxTransaction[] = stmtBlocks.map((block, index) => {
    const fitId = tagValue(block, 'FITID');
    const date = parseOfxDate(tagValue(block, 'DTPOSTED')) || parseOfxDate(tagValue(block, 'DTUSER')) || '';
    const amount = parseAmount(tagValue(block, 'TRNAMT'));
    const trnType = tagValue(block, 'TRNTYPE');
    const memo = tagValue(block, 'MEMO');
    const name = tagValue(block, 'NAME');
    const checkNum = tagValue(block, 'CHECKNUM');
    const description = [name, memo].filter(Boolean).join(' — ') || trnType || 'Lançamento sem descrição';

    let id = fitId || hashId([date, String(amount), description, String(index)]);
    if (seen.has(id)) id = `${id}-${index}`;
    seen.add(id);

    return {
      id,
      fitId,
      date,
      amount,
      type: amount >= 0 ? 'credit' : 'debit',
      trnType,
      description,
      memo,
      name,
      checkNum,
      raw: block.trim(),
    };
  });

  // Mais recentes primeiro
  transactions.sort((a, b) => (a.date < b.date ? 1 : a.date > b.date ? -1 : 0));

  return {
    bankId,
    accountId,
    accountType,
    currency,
    startDate,
    endDate,
    balance,
    balanceDate,
    transactions,
  };
};

export const readOfxFile = (file: File): Promise<OfxStatement> =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      try {
        const text = String(reader.result ?? '');
        if (!/<OFX>/i.test(text) && !/<STMTTRN>/i.test(text)) {
          reject(new Error('Arquivo não parece ser um extrato OFX válido.'));
          return;
        }
        resolve(parseOfx(text));
      } catch (e) {
        reject(e instanceof Error ? e : new Error('Falha ao ler o OFX.'));
      }
    };
    reader.onerror = () => reject(new Error('Não foi possível ler o arquivo.'));
    reader.readAsText(file, 'UTF-8');
  });
